import { useApp, useService, useSession } from "contexts";
import { useAnimation } from "framer-motion";
import { Auxiliars } from "helpers";
import { ICategory, ICategoryResponse, IMenu, IPotluck, IUser } from "interfaces";
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageType } from "types";
import { Enums, Routes } from "utils";

export const useSwiper = (potluck: IPotluck) => {
  const navigate = useNavigate();
  const controls = useAnimation();

  const { user } = useSession();
  const { setFeedback, t } = useApp();
  const { potluckService } = useService();

  const [menu, setMenu] = useState<IMenu[]>([]);
  const [selectedItems, setSelectedItems] = useState<ICategory[]>([]);
  const [isDone, setIsDone] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    if (isDone && unvotedCategories.length > 0) {
      const updatedMenu = Auxiliars.walkAndReplace<IMenu[]>(menu, [
        {
          "path": "categories.responses.user",
          "value": (user: IUser) => user._id
        }
      ]);

      const response = potluckService.voting(user.id, potluck._id, updatedMenu);

      if (response) {
        setFeedback({
          message: t.message.feedback.request_update_success,
          severity: Enums.EnumFeedback.Success
        } as MessageType);
      }
    }
  }, [isDone]);

  // Flatten unvoted categories with their menu context
  const unvotedCategories = useMemo(() => {
    return potluck.menu.flatMap((menu, menuIndex) =>
      menu.categories
        .filter((category) => !category.responses.some(r => r.user._id === user.id))
        .map((category, categoryIndex) => ({
          menuIndex,
          categoryIndex,
          category,
          menu,
        }))
    );
  }, [potluck, user.id]);

  useEffect(() => {
    if (unvotedCategories.length === 0) {
      setIsDone(true);
    }
  }, [unvotedCategories]);

  const current = unvotedCategories[currentIndex];

  const manageVoting = (menuIndex: number, categoryIndex: number, accepted: boolean) => {
    setMenu(prevMenu => {
      const updatedMenu = [...prevMenu];

      // Ensure menu item exists
      if (!updatedMenu[menuIndex]) {
        updatedMenu[menuIndex] = { configuration: current.menu.configuration, categories: [] } as IMenu;
      }

      const currentMenu = updatedMenu[menuIndex];

      // Ensure category exists
      if (!currentMenu.categories[categoryIndex]) {
        currentMenu.categories[categoryIndex] = {
          _id: current.category._id,
          responses: [],
        } as ICategory;
      }

      const currentCategory = currentMenu.categories[categoryIndex];

      // Check if user already responded
      const existingResponse = currentCategory.responses.find(
        (r) => r.user._id === user.id
      );

      if (existingResponse) {
        existingResponse.vote = accepted;
      } else {
        currentCategory.responses.push({
          user: { _id: user.id },
          vote: accepted,
        } as ICategoryResponse);
      }

      return updatedMenu;
    });
  };

  const addItem = (item: ICategory) => setSelectedItems((prev) => [...prev, item]);
  const removeItem = (index: number) => {
    manageVoting(unvotedCategories[index].menuIndex, unvotedCategories[index].categoryIndex, false);
    setSelectedItems((prev) => prev.filter((_, i) => index !== i));
  }

  const handleDecision = (accepted: boolean) => {
    if (accepted) {
      addItem(unvotedCategories[currentIndex].category);
    }

    manageVoting(current.menuIndex, current.categoryIndex, accepted);

    if (currentIndex + 1 >= unvotedCategories.length) {
      setIsDone(true);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleSwipe = async (accepted: boolean) => {
    await controls.start({
      x: accepted ? 300 : -300,
      opacity: 0,
      transition: { duration: 0.5 },
    });
    handleDecision(accepted);
    controls.set({ x: 0, opacity: 1 });
  };

  const handleGoBack = () => navigate(Routes.userdashboard);

  return {
    controls,
    currentMenuIndex: current?.menuIndex ?? 0,
    currentCategory: current?.category ?? null,
    handleGoBack,
    handleSwipe,
    isDone,
    removeItem,
    selectedItems,
  };
};
